import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import TopBarProgress from "react-topbar-progress-indicator";
import { useDispatch } from "react-redux";
import { create } from "../../../actions/mainCategory";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Alert from "../../../components/alert/alert.component";
import CustomButton, {
  CustomButtonClasses,
  CustomButtonTypes,
} from "../../../components/custom-button/custom-button";
import HeadingPrimary from "../../../components/custom-heading/heading-primary";
import HeadingSecondary from "../../../components/custom-heading/heading-secondary";
import CustomInput from "../../../components/custom-input/custom-input";
import MultiValueSelect from "./components/multi-value-select/multi-value-select";
import CustomTextArea from "../../../components/custom-input/custom-textarea";
import CustomRadioInput from "../../../components/custom-input/custom-radio-input";
import { createMainCategoryPageValidation } from "../../../components/form-validation-schemas/form-validation-schemas";
import ImageUploaderField from "../../../components/img-uploader-field/img-uploader-field";

const CreateMainCategory = () => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    slug: "",
    image: "",
    description: "",
    metaTag: [],
    metaDescription: "",
    metaKeyword: [],
    seoKeyword: [],
    show: true,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  const toBase64 = async (file) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    return new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageChange = async (e) => {
    const base64Image = await toBase64(e[0]);
    setFormData((prev) => ({ ...prev, image: base64Image }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetFormData = () => {
    setFormData({
      loading: false,
      name: "",
      displayName: "",
      slug: "",
      image: formData?.image,
      description: "",
      metaTag: [...formData.metaTag],
      metaDescription: "",
      metaKeyword: [...formData.metaKeyword],
      seoKeyword: [...formData.seoKeyword],
      show: true,
    });
  };

  const submitHandler = async () => {
    setLoading(true);
    try {
      await createMainCategoryPageValidation.validate(formData);
      const res = await dispatch(create(formData));
      if (res) {
        resetFormData();
        toast(`${res.data}`, { containerId: "B" });
        setError("");
      }
    } catch (error) {
      if (error.name === "ValidationError") {
        setError(error.errors[0]);
        toast.error(`${error?.errors[0]}`, { containerId: "B" });
      } else {
        setError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  TopBarProgress.config({
    barThickness: 4,
    barColors: {
      0: "rgb(51, 201, 45, .7)",
      ".3": "rgb(255, 77, 107, .7)",
      "1.0": "rgb(200, 125, 255, .7)",
    },
    shadowBlur: 5,
    shadowColor: "rgba(0, 0, 0, .5)",
  });

  const changeInputTags = (value, key) => {
    const data = value?.map((item) => item.value) || [];
    setFormData((prev) => ({
      ...prev,
      [key]: data,
    }));
  };

  return (
    <div className="container-scroller">
      {loading && <TopBarProgress />}

      <ToastContainer
      stacked 
        enableMultiContainer
        containerId={"B"}
        // position={toast.POSITION.TOP_RIGHT}
          position="top-right" // Hardcoded position
      />

      <div className="page-header">
        <HeadingPrimary>Create Main Category</HeadingPrimary>

        <div style={{ fontSize: ".9rem" }}>
          <Link to="/admin/main-categories">Main Categories</Link> /&nbsp;Create
          Main Category
        </div>
      </div>
      <span className="text-danger">{error}</span>
      <div className="row">
        <div className="col-12 grid-margin stretch-card">
          <div
            className="card p-0"
            style={{
              border: "1px solid #dcdcdc",
            }}
          >
            <div className="card-body p-0">
              <HeadingSecondary>Add a new main category</HeadingSecondary>
              <Alert />

              <form className="forms-sample m-3">
                <CustomInput
                  label="Name"
                  name="name"
                  type="text"
                  placeholder="Name"
                  labelStyles={{ fontWeight: "bold" }}
                  onChange={handleChange}
                  value={formData.name}
                />

                <CustomInput
                  label="Display Name"
                  name="displayName"
                  type="text"
                  placeholder="Display Name"
                  labelStyles={{ fontWeight: "bold" }}
                  onChange={handleChange}
                  value={formData.displayName}
                />

                <CustomInput
                  label="Slug"
                  name="slug"
                  type="text"
                  placeholder="slug"
                  labelStyles={{ fontWeight: "bold" }}
                  onChange={handleChange}
                  value={formData.slug}
                  required={true}
                />

                <ImageUploaderField
                  labelStyles={{ fontWeight: "bold" }}
                  label="Image upload"
                  buttonText="Choose image"
                  name="image"
                  onChange={(e) => handleImageChange(e)}
                />

                <MultiValueSelect
                  label="Meta Tags"
                  defaultValue={[]}
                  name="metaTags"
                  onChange={(value) => changeInputTags(value, "metaTag")}
                />

                <MultiValueSelect
                  label="Meta Keywords"
                  defaultValue={[]}
                  name="metaKeyword"
                  onChange={(value) => changeInputTags(value, "metaKeyword")}
                />

                <CustomTextArea
                  label="Meta Description"
                  labelStyles={{ fontWeight: "bold" }}
                  name="metaDescription"
                  rows={6}
                  value={formData.metaDescription}
                  onChange={handleChange}
                />

                <MultiValueSelect
                  label="Seo Keywords"
                  defaultValue={[]}
                  name="seoKeyword"
                  onChange={(value) => changeInputTags(value, "seoKeyword")}
                />

                <CustomTextArea
                  label="Seo Description"
                  labelStyles={{ fontWeight: "bold" }}
                  name="description"
                  rows={6}
                  value={formData.description}
                  onChange={handleChange}
                />

                <div className="form-group ">
                  <label
                    style={{ fontWeight: "bold" }}
                    className="col-sm-3 col-form-label"
                  >
                    Show in Header
                  </label>

                  <div className="col-sm-12">
                    <CustomRadioInput
                      label="Yes"
                      required={true}
                      type="radio"
                      id="radio1"
                      name="membershipRadios"
                      defaultChecked={true}
                      labelStyles={{ fontWeight: "bold" }}
                      value={formData.show}
                      onChange={() => setFormData({ ...formData, show: true })}
                    />
                  </div>

                  <div className="col-sm-5">
                    <CustomRadioInput
                      label="No"
                      required={true}
                      type="radio"
                      name="membershipRadios"
                      id="radio2"
                      labelStyles={{ fontWeight: "bold" }}
                      value={formData.show}
                      onChange={() => setFormData({ ...formData, show: false })}
                    />
                  </div>
                </div>

                <CustomButton
                  type={CustomButtonTypes.BUTTON}
                  className={CustomButtonClasses.PRIMARY}
                  onClick={() => submitHandler()}
                >
                  Submit
                </CustomButton>
                <Link to={"/admin/main-categories"}>
                  <button className="btn btn-light" type="button">
                    Cancel
                  </button>
                </Link>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMainCategory;
